import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { ExternalLink, Pencil } from 'lucide-react'
import { useTopBar } from '@/contexts/TopBarContext'
import { staffWebsiteApi, websiteError, websiteOverviewApi } from '@/api/website'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EnquiriesCard } from '@/features/website/EnquiriesCard'

function Switch({ label, hint, checked, disabled, onChange }: { label: string; hint: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <input type="checkbox" className="mt-1 rounded border-gray-300" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} />
      <span><span className="block text-sm font-medium text-gray-900">{label}</span><span className="block text-xs text-gray-500">{hint}</span></span>
    </label>
  )
}

/** CertSlate staff: switch Sites on for an organisation, build its site, hand it to the customer, or suspend it. */
export default function SiteDetailPage() {
  const { tenantId = '' } = useParams()
  const api = useMemo(() => staffWebsiteApi(tenantId), [tenantId])
  const qc = useQueryClient()
  const { data: rows } = useQuery({ queryKey: ['superadmin-sites'], queryFn: websiteOverviewApi.list })
  const { data: status } = useQuery({ queryKey: ['website-status', api.base], queryFn: api.getStatus })
  const row = rows?.find(r => r.tenantId === tenantId)
  useTopBar(<span className="text-sm font-medium text-gray-900">{row?.tenantName ?? 'Website'}</span>)

  const refresh = () => { qc.invalidateQueries({ queryKey: ['superadmin-sites'] }); qc.invalidateQueries({ queryKey: ['website-status', api.base] }) }
  const enable = useMutation({ mutationFn: (v: boolean) => api.setSitesEnabled(v), onSuccess: refresh })
  const create = useMutation({ mutationFn: () => api.createSite(), onSuccess: refresh })
  const editing = useMutation({ mutationFn: (v: boolean) => api.setCustomerEditing(v), onSuccess: refresh })
  const suspend = useMutation({ mutationFn: (v: boolean) => api.setSuspended(v), onSuccess: refresh })
  const error = [enable, create, editing, suspend].map(m => m.error).find(Boolean)

  if (!status) return <p className="text-gray-500">Loading…</p>
  const address = status.primaryHostname ? `https://${status.primaryHostname}` : null

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <Link to="/superadmin/sites" className="text-sm text-gray-500 hover:text-gray-800">&larr; All websites</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-1">{row?.tenantName ?? 'Website'}</h1>
        <p className="text-sm text-gray-500">{row?.tenantSlug}</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Controls</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Switch label="CertSlate Sites is on" hint="Switched on automatically when the subscription includes Sites; switch it on here for a pilot or complimentary site." checked={status.sitesEnabled} disabled={enable.isPending} onChange={v => enable.mutate(v)} />
          {status.sitesEnabled && !status.hasSite && (
            <Button disabled={create.isPending} onClick={() => create.mutate()}>{create.isPending ? 'Creating…' : 'Create the website'}</Button>
          )}
          {status.hasSite && (
            <>
              <Switch label="The customer can edit their website" hint="Off while you build it for them. Turn on to let their admins edit and publish." checked={row?.customerEditingEnabled ?? false} disabled={editing.isPending} onChange={v => editing.mutate(v)} />
              <Switch label="Suspended" hint="Takes the website down and stops the customer editing it." checked={status.status === 'Suspended'} disabled={suspend.isPending}
                onChange={v => { if (!v || confirm('Suspend this website? It will stop being served immediately.')) suspend.mutate(v) }} />
            </>
          )}
          {error && <p className="text-sm text-red-600">{websiteError(error).message}</p>}
        </CardContent>
      </Card>

      {status.hasSite && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Website</CardTitle>
            <span className="text-xs font-medium text-gray-700">{status.status}</span>
          </CardHeader>
          <CardContent className="space-y-3">
            {address && <p className="text-sm text-gray-700">Address: <span className="font-medium">{status.primaryHostname}</span></p>}
            {status.publishedAt && <p className="text-sm text-gray-500">Last published {new Date(status.publishedAt).toLocaleString('en-GB')}{status.hasUnpublishedChanges ? ' · unpublished changes' : ''}</p>}
            <div className="flex flex-wrap gap-2">
              <Button asChild><Link to={`/superadmin/sites/${tenantId}/edit`}><Pencil size={15} /> Open the editor</Link></Button>
              {address && status.status === 'Live' && <Button variant="outline" asChild><a href={address} target="_blank" rel="noreferrer"><ExternalLink size={15} /> View live site</a></Button>}
            </div>
          </CardContent>
        </Card>
      )}

      {status.hasSite && <EnquiriesCard api={api} />}
    </div>
  )
}
